import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Typography, TextField, MenuItem, Button, TablePagination,
  CircularProgress, Link, Tooltip, IconButton,
} from '@mui/material';
import { Refresh, FileDownload } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLedgerEntries } from '../../../store/slices/ledgerSlice';
import { useNavigate } from 'react-router-dom';
import ledgerService from '../../../services/ledgerService';

const EVENT_TYPES = [
  '', 'BILLABLE_MINUTE', 'RECHARGE', 'PAYOUT_INITIATED', 'PAYOUT_SETTLED',
  'PAYOUT_FAILED', 'REFUND', 'PARTIAL_FAILURE', 'REVERSAL', 'CLEARING_RELEASE',
];
const ACCOUNT_TYPES = [
  '', 'USER_ESCROW', 'ASTROLOGER_WITHDRAWABLE', 'PLATFORM_REVENUE',
  'GATEWAY_RECEIVED', 'PAYOUT_PENDING', 'SUSPENSE',
];

const formatPaise = (p) => `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN') : '-';

const entryColor = (type) => type === 'DEBIT' ? '#d32f2f' : '#388e3c';

const LedgerEntriesTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { entries, loading } = useSelector(s => s.ledger);

  const [filters, setFilters] = useState({
    event_type: '', account_type: '', entry_type: '',
    date_from: '', date_to: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    const params = { ...filters, page: page + 1, limit: rowsPerPage };
    // strip empty strings
    Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
    dispatch(fetchLedgerEntries(params));
  }, [dispatch, filters, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (key) => (e) => {
    setFilters(f => ({ ...f, [key]: e.target.value }));
    setPage(0);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
      const blob = await ledgerService.exportCSV(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger_export_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const items = entries.items || [];
  const total = entries.total || 0;

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
        <TextField select label="Event Type" size="small" value={filters.event_type}
          onChange={handleFilterChange('event_type')} sx={{ minWidth: 180 }}>
          {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t || '(All)'}</MenuItem>)}
        </TextField>
        <TextField select label="Account Type" size="small" value={filters.account_type}
          onChange={handleFilterChange('account_type')} sx={{ minWidth: 200 }}>
          {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t || '(All)'}</MenuItem>)}
        </TextField>
        <TextField select label="Entry Type" size="small" value={filters.entry_type}
          onChange={handleFilterChange('entry_type')} sx={{ minWidth: 130 }}>
          {['', 'DEBIT', 'CREDIT'].map(t => <MenuItem key={t} value={t}>{t || '(All)'}</MenuItem>)}
        </TextField>
        <TextField label="From" type="date" size="small" value={filters.date_from}
          onChange={handleFilterChange('date_from')} InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small" value={filters.date_to}
          onChange={handleFilterChange('date_to')} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={load} size="small" title="Refresh"><Refresh /></IconButton>
        <Button size="small" variant="outlined" startIcon={<FileDownload />}
          onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Box>

      {loading.entries ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#Seq</TableCell>
                <TableCell>Journal</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>ConsultID</TableCell>
                <TableCell>Tick</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">No entries found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((e) => (
                <TableRow key={e.entry_uuid} hover>
                  <TableCell>{e.sequence_no}</TableCell>
                  <TableCell>
                    <Tooltip title={e.journal_id}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/admin/ledger/journals/${e.journal_id}`)}
                        underline="hover"
                      >
                        {e.journal_id?.slice(0, 8)}…
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={e.entry_type}
                      size="small"
                      sx={{ bgcolor: entryColor(e.entry_type), color: 'white', fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{e.account_type}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ color: entryColor(e.entry_type), fontWeight: 'bold' }}>
                    {formatPaise(e.amount_paise)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{e.event_type}</Typography>
                  </TableCell>
                  <TableCell>{e.consultation_id ?? '-'}</TableCell>
                  <TableCell>{e.tick_number ?? '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap>{fmtDate(e.created_at)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default LedgerEntriesTable;
